import type { LexiconReportContext } from '@orborix/shared-types';
/**
 * Data layer del rediseño de Biblioteca (20.H). Une las dos fuentes reales
 * — catálogo (`lexiconApi`) y colección personal (`vocabApi` via useVocab) —
 * en un modelo de fila único (`LibWord`), y porta del bundle del diseñador:
 * cromo de bandas (rampa OKLCH), tiers acumulados de cobertura, registries de
 * decks/juegos con sus mínimos de material, y el armado del deck "Para hoy".
 *
 * Dos números que NUNCA se mezclan (D7 de la spec 20):
 *   · corpusCoverage — % del idioma que cubre cada tramo (dato fijo)
 *   · known/total    — progreso del usuario dentro del tramo
 */
import type { VocabTopic } from '@orborix/shared-types';
import type {
  Coverage,
  LexiconMeaning,
  LexiconSet,
  LexiconWord,
  PhrasalMeta,
  VerbForms,
} from '../../lib/lexiconApi.js';
import type { FamiliarityTier } from '../../components/familiarity/Familiarity.js';
import type { CefrLevel, VocabEntry } from '../vocabulary/vocabData.js';
import type { LibDeckIconName, LibGameIconName } from './libIcons.js';

// ─── Bandas: cromo del bundle (rampa OKLCH lima→verde→cian→violeta→naranja) ──
export interface LibBand {
  band: number;
  label: string;
  range: readonly [number, number];
  accent: string;
}

export const LIB_BANDS: readonly LibBand[] = [
  { band: 500, label: 'Top 500', range: [1, 500], accent: 'oklch(0.91 0.19 117)' },
  { band: 1000, label: 'Top 1000', range: [501, 1000], accent: 'oklch(0.85 0.19 140)' },
  { band: 2000, label: 'Top 2000', range: [1001, 2000], accent: 'oklch(0.84 0.12 200)' },
  { band: 5000, label: 'Top 5000', range: [2001, 5000], accent: 'oklch(0.72 0.14 295)' },
  { band: 99999, label: 'Más allá', range: [5001, 99999], accent: 'oklch(0.76 0.13 55)' },
];

/**
 * La frontera del A0 (plan 48 F2): «Primeros pasos» = la lista `starter-1`.
 * NO es un tramo de frecuencia: sus palabras están repartidas por todas las
 * bandas, así que no vive en `LIB_BANDS` (ni `libBandOf` la devuelve nunca) y
 * su `band` es un centinela (0) que no choca con ningún tope de rank. Sólo
 * existe como tier del medidor cuando la cobertura trae `starter`.
 */
export const LIB_STARTER_BAND: LibBand = {
  band: 0,
  label: 'Primeros pasos',
  range: [0, 0],
  accent: 'var(--coral)',
};

export function isStarterTier(t: { band: number }): boolean {
  return t.band === LIB_STARTER_BAND.band;
}

export function libBandOf(rank: number): LibBand {
  return LIB_BANDS.find((b) => rank >= b.range[0] && rank <= b.range[1]) ?? LIB_BANDS[LIB_BANDS.length - 1]!;
}

// ─── Fila unificada (catálogo ∪ colección) ───────────────────────────
/** Un significado del diccionario nuevo (v2): lo que muestra cada etiqueta de la ficha D. */
export type LibMeaning = LexiconMeaning;

export interface LibWord {
  reportContext?: LexiconReportContext;
  id: string;
  text: string;
  tr: string;
  ipa: string | null;
  pos: string | null;
  cefr: CefrLevel | null;
  rank: number;
  kind: string;
  senses: string[];
  definition: string | null;
  example: string | null;
  verbForms: VerbForms | null;
  phrasal: PhrasalMeta | null;
  /** Dominada: conocida (la dijo hablando O la marcó "ya la sé"). Mueve el medidor. */
  dom: boolean;
  /** De estar dominada, si la PRODUJO (dicha en charla/Decila) vs sólo marcada. */
  said: boolean;
  /** La marcó "ya la sé" a mano (permite des-marcar; sólo si no la produjo). */
  manual: boolean;
  saved: boolean;
  /** Familiaridad (sólo cuando la fila viene del lado vocab). */
  fam: FamiliarityTier | null;
  /** "Me cuesta" (vocab.difficulty). */
  hard: boolean;
  fav: boolean;
  /**
   * Frase: lo guardado que no se trata como palabra del catálogo (sin «Ya la
   * sé» ni «Guardar» en la ficha, fuera de los juegos de una palabra). En las
   * copias de Lo mío lo decide primero el diccionario y, si no la tiene, el
   * texto (`vocabIsPhrase`); la ficha del diccionario siempre es palabra
   * (`libOverCollection`).
   */
  isPhrase: boolean;
  /** Detectada por el coach (origin='app'), no guardada a mano. */
  fromSessions: boolean;
  /** id de vocab_entries cuando la fila existe en la colección (PATCH/DELETE). */
  vocabId: string | null;
  /** ids de las etiquetas del usuario (sólo el lado vocab; el catálogo no tiene). */
  tags: string[];
  /** Tema que le puso el coach (sólo el lado vocab). */
  topic: VocabTopic | null;
  /** La charla en la que el coach la detectó (sólo el lado vocab). */
  session: string | null;
  /**
   * Los significados del diccionario nuevo (v2), el principal primero: con
   * más de uno, la ficha es la D. Null con el diccionario de hoy (v1), en las
   * copias de Lo mío y en lo que no sale del catálogo. `tr`, `example` y el
   * resto de arriba son SIEMPRE los del principal: es lo que se guarda y lo
   * que usan los juegos, elija lo que elija el alumno en la ficha.
   */
  meanings: LibMeaning[] | null;
  /** Traducción del ejemplo del principal (v2). */
  exampleEs: string | null;
  /** El significado que encontró el buscador en español: el que muestra la fila y en el que abre la ficha. */
  matchedMeaningId: string | null;
}

export function libFromLexicon(w: LexiconWord): LibWord {
  return {
    id: w.id,
    text: w.text,
    tr: w.tr,
    ipa: w.ipa,
    pos: w.pos,
    cefr: w.cefr,
    rank: w.rank,
    kind: w.kind,
    senses: w.senses,
    definition: w.definition,
    example: w.example,
    verbForms: w.verbForms,
    phrasal: w.phrasal,
    dom: w.used,
    said: w.said,
    manual: w.manual,
    saved: w.saved,
    fam: null,
    hard: false,
    fav: false,
    isPhrase: false,
    fromSessions: false,
    vocabId: null,
    tags: [],
    topic: null,
    session: null,
    ...(w.reportContext ? { reportContext: w.reportContext } : {}),
    meanings: w.meanings,
    exampleEs: w.exampleEs,
    matchedMeaningId: w.matchedMeaningId,
  };
}

/**
 * Si una copia de Lo mío es "frase" (decisión 86). Primero el diccionario
 * activo, que el backend consulta por cada entrada (`inDictionary`, con la
 * misma clave con la que la ficha le pregunta): lo que está ahí —una palabra o
 * un verbo frasal como `give up`— es palabra, tenga espacio o el tipo que
 * tenga. Lo que no está sigue la regla de antes: frase por su tipo o por tener
 * un espacio. Así una palabra suelta que el diccionario no tiene
 * (`treadmill`) sigue siendo palabra, y una frase del coach, frase.
 */
function vocabIsPhrase(e: VocabEntry): boolean {
  if (e.inDictionary) return false;
  return e.entryType === 'phrase' || e.entryType === 'expression' || e.en.trim().includes(' ');
}

export function libFromVocab(e: VocabEntry): LibWord {
  return {
    id: `v|${e.id}`,
    text: e.en,
    tr: e.es ?? '',
    ipa: e.ipa,
    pos: e.pos,
    cefr: e.cefr,
    rank: 99999,
    kind: e.entryType,
    senses: [],
    definition: null,
    example: e.example,
    verbForms: null,
    phrasal:
      e.particle != null
        ? { base: e.en.split(' ')[0] ?? e.en, particle: e.particle, separable: e.separable ?? false, transitive: false }
        : null,
    dom: false,
    said: false,
    manual: false,
    // «Guardada» es sólo la que guardó a mano (Emmanuel, 22/10, #603): la que detectó el coach
    // está en Lo mío pero muestra «Guardar», y al tocarlo pasa a «Mis guardadas».
    saved: e.origin !== 'app',
    fam: e.familiarity,
    hard: e.difficulty,
    fav: e.favorite,
    isPhrase: vocabIsPhrase(e),
    fromSessions: e.origin === 'app',
    vocabId: e.id,
    tags: e.tags,
    topic: e.topic,
    session: e.session,
    meanings: null,
    exampleEs: null,
    matchedMeaningId: null,
  };
}

// ─── Diccionario nuevo (v2): la ficha D ──────────────────────────────
/**
 * La ficha del diccionario con lo del alumno encima (decisión 73, f): el
 * contenido léxico sale de la entrada del diccionario y la identidad y las
 * marcas, de la copia de Lo mío. Así la ficha sigue encontrando la entrada de
 * la colección por `vocabId` (etiquetas, favorita, «me cuesta», tema) y el
 * guardado sigue siendo por palabra.
 */
export function libOverCollection(dict: LibWord, copy: LibWord): LibWord {
  return {
    ...dict,
    id: copy.id,
    saved: copy.saved,
    fam: copy.fam,
    hard: copy.hard,
    fav: copy.fav,
    // `isPhrase` es del diccionario (false): un frasal guardado con espacio
    // («give up») es su entrada, no una frase (decisión 84).
    fromSessions: copy.fromSessions,
    vocabId: copy.vocabId,
    tags: copy.tags,
    topic: copy.topic,
    session: copy.session,
    matchedMeaningId: copy.matchedMeaningId,
  };
}

/** El significado `id` de la palabra. Tira si no es suyo: un id ajeno es un bug, no "el principal". */
export function libMeaning(w: LibWord, id: string): LibMeaning {
  const m = w.meanings?.find((x) => x.id === id);
  if (!m) throw new Error(`libMeaning: «${w.text}» no tiene el significado ${id}`);
  return m;
}

/** La traducción de una fila: la del significado que encontró el buscador (v2), o la principal. */
export function libRowTr(w: LibWord): string {
  return w.matchedMeaningId && w.meanings ? libMeaning(w, w.matchedMeaningId).tr : w.tr;
}

/** Un pedazo del texto de una etiqueta: la traducción, o una aclaración (lo que iba entre paréntesis). */
export interface LibChipPart {
  text: string;
  aside: boolean;
}

/**
 * Una etiqueta de la ficha D en sus pedazos, para la forma C
 * (ETIQUETAS-EN-LA-FICHA.md, decisión 78): lo que va entre paréntesis es una
 * aclaración, que se dibuja sin ellos, más chica y en gris; el resto es la
 * traducción. Puede haber dos («se omite (en español) (objeto)») o ir en el
 * medio («tener (un) seguro»). Port de `segmentos` de la maqueta
 * (`docs/design/lexicon/tools/etiquetas-en-la-ficha/formas.mjs`), con una
 * excepción: un paréntesis pegado a la palabra («inclinar(se)», «apropiar(se)
 * de») es parte de ella, no una aclaración, y queda como está. Pura.
 */
export function libChipParts(label: string): LibChipPart[] {
  const parts: LibChipPart[] = [];
  let from = 0;
  for (const m of label.matchAll(/\(([^()]*)\)/g)) {
    if (m.index > 0 && !/\s/.test(label.charAt(m.index - 1))) continue;
    const before = label.slice(from, m.index).trim();
    if (before) parts.push({ text: before, aside: false });
    const aside = m[1]!.trim();
    if (aside) parts.push({ text: aside, aside: true });
    from = m.index + m[0].length;
  }
  const rest = label.slice(from).trim();
  if (rest) parts.push({ text: rest, aside: false });
  return parts.length > 0 ? parts : [{ text: label, aside: false }];
}

/** Las etiquetas de la ficha D: dos filas como máximo, 6 px entre ellas (LA-FICHA-EN-LA-APP.md, «Las medidas»). */
export const LIB_CHIP_ROWS = 2;
export const LIB_CHIP_GAP = 6;

/**
 * Cuántas etiquetas de la ficha D entran en `rows` filas de ancho `width`,
 * ubicadas como las ubica el navegador (de izquierda a derecha, `gap` entre
 * ellas, la que no entra pasa a la fila siguiente, ninguna más ancha que la
 * fila: se corta con «…»). Si no entran todas, la última fila deja lugar para
 * «otros» (`otherWidth`), sacando del final las que haga falta. Siempre queda
 * al menos una: la del principal. Port de `cuantasEntran` de la maqueta
 * (`docs/design/lexicon/tools/la-ficha-en-la-app/pagina.mjs`). Pura.
 */
export function libChipsFit(
  widths: readonly number[],
  width: number,
  opts: { otherWidth?: number; gap?: number; rows?: number } = {},
): number {
  const gap = opts.gap ?? LIB_CHIP_GAP;
  const rows = opts.rows ?? LIB_CHIP_ROWS;
  const rowsOf = (ws: readonly number[]) => {
    let r = 1;
    let x = 0;
    for (const w0 of ws) {
      const w = Math.min(w0, width);
      if (x === 0) x = w;
      // el navegador no deja pasar nada: una etiqueta que se pasa de la fila
      // por medio píxel baja a la siguiente (y «otros» a una tercera fila, en
      // `carry`). Mide en 1/64 px, así que sólo una centésima, por el redondeo
      // de la suma (ETIQUETAS-EN-LA-FICHA.md)
      else if (x + gap + w <= width + 0.01) x += gap + w;
      else {
        r++;
        x = w;
      }
    }
    return r;
  };
  if (rowsOf(widths) <= rows) return widths.length;
  if (opts.otherWidth == null) throw new Error('libChipsFit: falta el ancho de «otros»');
  for (let k = widths.length - 1; k >= 1; k--) {
    if (rowsOf([...widths.slice(0, k), opts.otherWidth]) <= rows) return k;
  }
  return 1;
}

// ─── La ficha rediseñada: la D (LA-FICHA-REDISENADA.md, decisión 80) ──
/** Un tipo de palabra dicho en claro: su nombre entero, qué es, y las palabras más frecuentes de ese tipo. */
export interface LibPosInfo {
  name: string;
  what: string;
  examples: readonly string[];
}

/**
 * El tipo de palabra escrito entero, por la etiqueta corta que trae la
 * palabra (`POS_ES` de lexiconApi.ts: «det.» → «determinante»). Es SÓLO de
 * dibujo: speak guarda la etiqueta corta al guardar una palabra
 * (`partOfSpeech: w.pos`), así que `POS_ES` no cambia. Los textos son los de la
 * maqueta (`EN_CLARO` de tools/la-ficha-redisenada/fichas.mjs), en español
 * neutro; «locución» pasa a «expresión». Los ejemplos son las palabras más
 * frecuentes de cada tipo en el diccionario (`ej` de
 * la-ficha-redisenada/datos.json, que los saca del diccionario); el test de la
 * maqueta los compara.
 */
export const LIB_POS: Readonly<Record<string, LibPosInfo>> = {
  'sust.': { name: 'sustantivo', what: 'nombra una persona, un lugar, una cosa o una idea', examples: ['one', 'time', 'people'] },
  verbo: { name: 'verbo', what: 'dice una acción o un estado', examples: ['be', 'have', 'do'] },
  'adj.': { name: 'adjetivo', what: 'dice cómo es algo o alguien', examples: ['good', 'first', 'new'] },
  'adv.': { name: 'adverbio', what: 'dice cómo, cuándo, dónde o cuánto', examples: ['not', 'there', 'when'] },
  'pron.': { name: 'pronombre', what: 'ocupa el lugar de un sustantivo', examples: ['it', 'you', 'he'] },
  'prep.': { name: 'preposición', what: 'une una palabra con otra: dónde, cuándo, de quién', examples: ['of', 'in', 'for'] },
  'conj.': { name: 'conjunción', what: 'une palabras o partes de una oración', examples: ['and', 'as', 'but'] },
  'art.': { name: 'artículo', what: 'va antes de un sustantivo', examples: [] },
  'det.': { name: 'determinante', what: 'va antes de un sustantivo y dice cuál, cuánto o de qué tipo', examples: ['the', 'a', 'this'] },
  'interj.': { name: 'interjección', what: 'una exclamación o una respuesta corta', examples: ['no', 'yes', 'please'] },
  'núm.': { name: 'número', what: 'cuenta u ordena', examples: ['million', 'trillion'] },
  abreviatura: { name: 'abreviatura', what: 'la forma corta de una palabra o de un nombre', examples: ['wed', 'mr', 'info'] },
  locución: { name: 'expresión', what: 'varias palabras (o una) que se usan juntas como una sola', examples: ['gee', 'goodnight', 'break up'] },
  prefijo: { name: 'prefijo', what: 'se pega al principio de otra palabra', examples: ['non', 'ultra', 'micro'] },
  contracción: { name: 'contracción', what: 'dos palabras dichas juntas, como una', examples: ['gimme'] },
  símbolo: { name: 'símbolo', what: 'un signo que se lee como una palabra', examples: ['i', 'at'] },
  partícula: { name: 'partícula', what: 'una palabra corta que acompaña a un verbo', examples: ['to'] },
};

/** La etiqueta corta de los verbos: la conjugación se ve sólo con un significado que lo es. */
export const LIB_POS_VERB = 'verbo';

/** Lo que dice un tipo de palabra, o null si la etiqueta no es del catálogo. */
export function libPosInfo(label: string): LibPosInfo | null {
  return Object.hasOwn(LIB_POS, label) ? LIB_POS[label]! : null;
}

/**
 * El tipo de palabra escrito entero, para dibujarlo (la ficha y Decila). Una
 * etiqueta que no es del catálogo (las copias de Lo mío guardan la que puso el
 * coach: «noun phrase», «idiom») se dibuja como está, como hasta ahora. Pura.
 */
export function libPosName(label: string): string {
  return libPosInfo(label)?.name ?? label;
}

/** Qué quiere decir un nivel, como lo dice el filtro de Explorar (A2 → «básico»). */
export function libCefrHint(cefr: CefrLevel): string {
  const l = LIB_CEFR.find((x) => x.id === cefr);
  if (!l) throw new Error(`libCefrHint: nivel desconocido ${cefr}`);
  return l.hint;
}

/**
 * «También» sin lo que ya se ve (la regla de la maqueta, `tambien` de
 * fichas.mjs): de las otras traducciones, cortadas en las comas, se va la que
 * es la traducción o una de sus aclaraciones, y las repetidas. `such`: «tan, de
 * tal manera» debajo de «tan» deja «de tal manera»; `live`: «residir» debajo de
 * «vivir (residir)» no deja nada. El dato repetido se arregla en el catálogo;
 * esto es sólo el dibujo. Pura.
 */
export function libAlso(tr: string, alts: readonly string[]): string[] {
  const norm = (s: string) => s.trim().toLowerCase();
  const seen = new Set(libChipParts(tr).map((p) => norm(p.text)));
  const out: string[] = [];
  for (const a of alts) {
    for (const x of a.split(/,\s*/)) {
      const t = x.trim();
      if (t && !seen.has(norm(t)) && !out.some((y) => norm(y) === norm(t))) out.push(t);
    }
  }
  return out;
}

/**
 * El tramo de frecuencia en palabras, para la explicación del encabezado
 * (`frecuencia` de la maqueta): «está entre las 500 palabras más usadas del
 * inglés (puesto 100)». El puesto («palabra #100») pasa acá. Pura.
 */
export function libBandText(band: LibBand, rank: number): string {
  const where =
    band.range[1] >= 99999
      ? 'no está entre las 5.000 palabras más usadas del inglés'
      : `está entre las ${band.range[1].toLocaleString('es-AR')} palabras más usadas del inglés`;
  return `${where} (puesto ${rank.toLocaleString('es-AR')})`;
}

/**
 * Dónde está la palabra en su ejemplo, para resaltarla: la palabra o una forma
 * suya (su conjugación, +s/+es/+ed/+ing y las mismas reglas que El Hueco), sin
 * distinguir mayúsculas y como palabra entera. En una de varias palabras
 * (`give up`) se conjuga la primera. Null si no aparece: el ejemplo va sin
 * resaltar.
 */
export function libExampleMark(w: LibWord, example: string): { start: number; end: number } | null {
  const words = w.text.trim().toLowerCase().split(/\s+/);
  const head = words[0];
  if (!head) return null;
  const rest = words.slice(1).join(' ');
  const phrases = new Set<string>();
  for (const f of libInflections(w, head)) {
    // las formas de `verbForms` de un phrasal ya traen la partícula
    phrases.add(rest && !f.includes(' ') ? `${f} ${rest}` : f);
  }
  const lower = example.toLowerCase();
  // alguna letra que cambia de largo al pasar a minúscula: los índices ya no son los del ejemplo
  if (lower.length !== example.length) return null;
  let best: { start: number; end: number } | null = null;
  for (const p of phrases) {
    let from = 0;
    for (let i = lower.indexOf(p, from); i >= 0; i = lower.indexOf(p, from)) {
      from = i + 1;
      const end = i + p.length;
      if (WORD_CHAR.test(lower[i - 1] ?? '') || WORD_CHAR.test(lower[end] ?? '')) continue;
      // la primera en la oración; a igual comienzo, la más larga («runs» antes que «run»)
      if (!best || i < best.start || (i === best.start && end > best.end)) best = { start: i, end };
      break;
    }
  }
  return best;
}

/** Lo que forma parte de una palabra (para el borde de palabra entera del resaltado). */
const WORD_CHAR = /[\w'À-ÿ-]/;

// ─── Cobertura: tiers ACUMULADOS para el sheet del medidor ───────────
// Para un A0 (cobertura con `starter`) va primero el tier «Primeros pasos»:
// SUELTO, no acumulado — sus palabras no se suman a los Top 500… (que quedan
// idénticos) porque ya están contadas en sus bandas de frecuencia.
export interface LibCovTier {
  band: number;
  label: string;
  known: number;
  /** De `known` (acumulado), producidas (dichas) vs auto-reportadas (marcadas). */
  said: number;
  marked: number;
  total: number;
  pct: number; // 0..100 (progreso del user, acumulado)
  corpusPct: number; // 0..100 (dato fijo del idioma)
  blurbEs: string;
  accent: string;
  frontier: boolean;
}

export interface LibCoverage {
  tiers: LibCovTier[];
  frontier: LibCovTier | null;
  totalKnown: number;
  /** known per-band (para los separadores de la lista). */
  bandKnown: Record<number, number>;
}

const DONE_PCT = 92;

export function libCoverage(coverage: Coverage | null): LibCoverage {
  if (!coverage) return { tiers: [], frontier: null, totalKnown: 0, bandKnown: {} };
  let kSum = 0;
  let sSum = 0;
  let mSum = 0;
  let tSum = 0;
  const tiers: LibCovTier[] = [];
  const bandKnown: Record<number, number> = {};
  for (const chrome of LIB_BANDS) {
    const cov = coverage.bands.find((b) => b.band === chrome.band);
    bandKnown[chrome.band] = cov?.known ?? 0;
    if (chrome.band === 99999) continue;
    kSum += cov?.known ?? 0;
    sSum += cov?.said ?? 0;
    mSum += cov?.marked ?? 0;
    tSum += cov?.total ?? 0;
    tiers.push({
      band: chrome.band,
      label: chrome.label,
      known: kSum,
      said: sSum,
      marked: mSum,
      total: tSum,
      pct: tSum === 0 ? 0 : Math.round((kSum / tSum) * 100),
      corpusPct: Math.round((cov?.corpusCoverage ?? 0) * 100),
      blurbEs: cov?.blurbEs ?? '',
      accent: chrome.accent,
      frontier: false,
    });
  }
  if (coverage.starter) {
    const st = coverage.starter;
    tiers.unshift({
      band: LIB_STARTER_BAND.band,
      label: LIB_STARTER_BAND.label,
      known: st.known,
      said: st.said,
      marked: st.marked,
      total: st.total,
      // Del known/total, como los demás tiers (el `pct` del server es fracción 0..1).
      pct: st.total === 0 ? 0 : Math.round((st.known / st.total) * 100),
      // Sin dato de cobertura del idioma: es una lista, no un tramo de frecuencia.
      corpusPct: 0,
      blurbEs: 'Las palabras con las que arrancás a charlar.',
      accent: LIB_STARTER_BAND.accent,
      frontier: false,
    });
  }
  const fi = tiers.findIndex((t) => t.pct < DONE_PCT);
  tiers.forEach((t, i) => {
    t.frontier = i === (fi < 0 ? tiers.length - 1 : fi);
  });
  const frontier = tiers.find((t) => t.frontier) ?? tiers[tiers.length - 1] ?? null;
  const beyondKnown = bandKnown[99999] ?? 0;
  return { tiers, frontier, totalKnown: kSum + beyondKnown, bandKnown };
}

// ─── Filtros de Explorar (chips de tipo + Me faltan + CEFR) ──────────
// Eje primario = "tipo de palabra" (una dimensión, mutuamente excluyente).
// "Irregulares" NO es hermano de "Verbos" (irregular ⊂ verbo), así que vive
// en el sub-eje `verbKind` que aparece sólo al elegir Verbos. Phrasal sí queda
// en primer nivel: es la categoría de estudio estrella (alto valor/visibilidad)
// aunque técnicamente también sea un verbo.
export type LibTypeChip = 'all' | 'verbs' | 'phrasal';

export const LIB_TYPE_CHIPS: ReadonlyArray<{ id: LibTypeChip; label: string }> = [
  { id: 'all', label: 'Todas' },
  { id: 'verbs', label: 'Verbos' },
  { id: 'phrasal', label: 'Phrasal' },
];

/** Sub-eje de Verbos: detalle de conjugación (sólo aplica con type='verbs'). */
export type LibVerbKind = 'all' | 'regular' | 'irregular';

export const LIB_VERB_KINDS: ReadonlyArray<{ id: LibVerbKind; label: string }> = [
  { id: 'all', label: 'Todos' },
  { id: 'regular', label: 'Regulares' },
  { id: 'irregular', label: 'Irregulares' },
];

export const LIB_CEFR: ReadonlyArray<{ id: CefrLevel; hint: string }> = [
  { id: 'A1', hint: 'inicial' },
  { id: 'A2', hint: 'básico' },
  { id: 'B1', hint: 'medio' },
  { id: 'B2', hint: 'medio alto' },
  { id: 'C1', hint: 'avanzado' },
  { id: 'C2', hint: 'dominio' },
];

export interface LibExploreFilter {
  type: LibTypeChip;
  /** Detalle de conjugación; sólo se usa cuando type='verbs'. */
  verbKind: LibVerbKind;
  missing: boolean;
  /** Single-select: el endpoint del catálogo acepta UN cefr por query. */
  cefr: CefrLevel | null;
  /**
   * Una lista en vez del catálogo entero (plan 48 F2): `starter-1` = el chip
   * «Primeros pasos», que sólo se ofrece cuando la cobertura trae `starter`
   * (alumno A0). Se combina con los demás filtros, sobre todo con Me faltan.
   */
  set: LexiconSet | null;
}

/** Estado "sin filtros" — lo que deja el botón "Limpiar filtros" (muestra todo). */
export function emptyExploreFilter(): LibExploreFilter {
  return { type: 'all', verbKind: 'all', missing: false, cefr: null, set: null };
}

/**
 * Estado de ENTRADA de Explorar: "Me faltan" ON. Lo primero que ve el usuario
 * son las palabras más frecuentes que todavía NO domina (su frontera), no las
 * básicas que ya domina. El toggle sigue para ver todo el catálogo.
 */
export function defaultExploreFilter(): LibExploreFilter {
  return { type: 'all', verbKind: 'all', missing: true, cefr: null, set: null };
}

/** ¿Hay algún filtro activo? (decide si los separadores de banda muestran el
 *  conteo de cobertura — sólo tiene sentido sobre el catálogo COMPLETO). */
export function isExploreFiltered(f: LibExploreFilter): boolean {
  return f.type !== 'all' || f.missing || f.cefr != null || f.set != null;
}

/** Mapea el filtro de UI al query server-side de `fetchLexicon`. */
export function exploreFilterToQuery(f: LibExploreFilter): {
  pos?: string;
  kind?: string;
  irregular?: boolean;
  state?: 'unused';
  cefr?: string;
  set?: LexiconSet;
} {
  const q: ReturnType<typeof exploreFilterToQuery> = {};
  if (f.type === 'verbs') {
    q.pos = 'verb';
    if (f.verbKind === 'irregular') q.irregular = true;
    else if (f.verbKind === 'regular') q.irregular = false;
  } else if (f.type === 'phrasal') {
    q.kind = 'phrasal_verb';
  }
  if (f.missing) q.state = 'unused';
  if (f.cefr) q.cefr = f.cefr;
  if (f.set) q.set = f.set;
  return q;
}

// ─── Práctica: registries de decks y juegos (bundle, tal cual) ───────
export type LibraryDeckId =
  | 'guardadas'
  | 'debiles'
  | 'dormidas'
  | 'frontera'
  | 'sesion'
  | 'pron'
  | 'favoritas';

export interface LibDeck {
  id: LibraryDeckId;
  label: string;
  icon: LibDeckIconName;
  desc: string;
}

export const LIB_DECKS: readonly LibDeck[] = [
  // Primero (Emmanuel, 20/10): todas las palabras que guardó, para jugarlas cuando quiera. En un
  // teléfono entran ~3 chips: de último no se encontraba.
  { id: 'guardadas', label: 'Mis guardadas', icon: 'saved', desc: 'todas las que guardaste' },
  { id: 'debiles', label: 'Débiles', icon: 'weak', desc: 'las que marcaste o fallaste' },
  { id: 'dormidas', label: 'Dormidas', icon: 'frost', desc: 'afianzadas que se enfriaron' },
  { id: 'frontera', label: 'Frontera', icon: 'flag', desc: 'tus próximas palabras' },
  { id: 'sesion', label: 'Última sesión', icon: 'chat', desc: 'lo que salió en tu charla' },
  { id: 'pron', label: 'Pronunciación', icon: 'wave', desc: 'las que te cuesta decir' },
  { id: 'favoritas', label: 'Favoritas', icon: 'star', desc: 'tus elegidas' },
];

export type LibraryGameId = 'repaso' | 'parejas' | 'hueco' | 'decila' | 'prondrill';

export interface LibGameDef {
  id: LibraryGameId;
  label: string;
  icon: LibGameIconName;
  /** Mínimo de material para que el juego sea jugable con un deck. */
  min: number;
  /** Sólo la producción oral mueve el medidor de cobertura (D4). */
  production: boolean;
  desc: string;
  star?: boolean;
  soon?: boolean;
}

export const LIB_GAMES: readonly LibGameDef[] = [
  { id: 'repaso', label: 'Repaso', icon: 'cards', min: 2, production: false, desc: 'flashcards EN↔ES con audio' },
  { id: 'parejas', label: 'Parejas', icon: 'pairs', min: 4, production: false, desc: 'uní inglés y español' },
  // min 4 = LIB_CLOZE_OPTIONS: cada ronda muestra la correcta + 3 del mismo deck.
  // Con 3 se habilitaba y abría directo en «0 de 0». El mínimo de palabras no
  // alcanza solo (puede no haber oración para completar): ver `libGameShortfall`.
  { id: 'hueco', label: 'El Hueco', icon: 'gap', min: 4, production: false, desc: 'completá la oración' },
  { id: 'decila', label: 'Decila', icon: 'say', min: 3, production: true, desc: 'decila al micrófono', star: true },
  { id: 'prondrill', label: 'Pronunciación', icon: 'wave', min: 1, production: true, desc: 'drill por fonema', soon: true },
];

export function libGame(id: LibraryGameId): LibGameDef {
  const g = LIB_GAMES.find((x) => x.id === id);
  if (!g) throw new Error(`libGame: juego desconocido '${id}'`);
  return g;
}

// ─── Decks: contenido a partir de la colección + el catálogo ─────────
/**
 * Decks que salen de la colección (client-side). "Frontera" no está acá:
 * necesita el catálogo (palabras no dominadas de la banda frontera) y lo
 * resuelve el caller con un fetch dedicado.
 */
export function libDeckFromCollection(deckId: Exclude<LibraryDeckId, 'frontera'>, my: readonly LibWord[]): LibWord[] {
  // Las guardadas a mano (no las que detectó el coach), palabras y no frases: las que juegan los juegos.
  if (deckId === 'guardadas') return my.filter((w) => !w.fromSessions && !w.isPhrase);
  if (deckId === 'debiles') return my.filter((w) => w.hard);
  if (deckId === 'dormidas') return my.filter((w) => w.fam === 'dormida');
  if (deckId === 'favoritas') return my.filter((w) => w.fav && !w.isPhrase);
  if (deckId === 'pron') return my.filter((w) => w.hard && !w.isPhrase); // v1: proxy "me cuesta"; el deck fonético real llega con Pronunciación (v1.5)
  if (deckId === 'sesion') return my.filter((w) => w.fromSessions || w.fam === 'nueva').slice(0, 8);
  return [];
}

export interface LibTodayDeck {
  id: LibraryDeckId | 'hoy';
  label: string;
  game: LibraryGameId;
  words: LibWord[];
  why: string[];
  coldStart?: boolean;
}

/** El deck sugerido del día, con el porqué explícito (heurística v1, §4.3). */
export function libTodayDeck(my: readonly LibWord[], frontierWords: readonly LibWord[]): LibTodayDeck {
  const weak = my.filter((w) => w.hard).slice(0, 5);
  const dormant = my.filter((w) => w.fam === 'dormida' && !weak.includes(w)).slice(0, 3);
  const words = [...weak, ...dormant];
  if (words.length >= 3) {
    return {
      id: 'hoy',
      label: 'Para hoy',
      game: 'decila',
      words,
      why: [
        ...(weak.length ? [`${weak.length} que te cuestan`] : []),
        ...(dormant.length ? [`${dormant.length} dormidas`] : []),
      ],
    };
  }
  // Cold-start: sin historia suficiente, cae en Frontera (siempre hay deck).
  const frontier = frontierWords.slice(0, 8);
  return {
    id: 'frontera',
    label: 'Frontera: tus próximas palabras',
    game: 'repaso',
    words: frontier,
    why: [`${frontier.length} palabras nuevas para vos`, 'para empezar a reconocerlas'],
    coldStart: true,
  };
}

// ─── El Hueco: rondas cloze desde el ejemplo de cada palabra ─────────
/** Opciones por ronda: la correcta + 3 incorrectas sacadas del mismo deck. */
export const LIB_CLOZE_OPTIONS = 4;

export interface LibClozeRound {
  word: LibWord;
  /** La oración partida en el hueco: `before` + `filled` + `after`. */
  before: string;
  /** La palabra tal como está en la oración: puede venir conjugada («took» para *take*). */
  filled: string;
  after: string;
  /** Lo que hay que elegir: la forma base, que es lo que dicen las opciones. */
  answer: string;
  options: string[];
}

/** La forma que muestra la opción: primera palabra, sin el «to» del infinitivo. */
function libClozeForm(w: LibWord): string {
  return w.text.trim().replace(/^to /i, '').split(/\s+/)[0] ?? '';
}

/**
 * Formas en las que la palabra puede aparecer en su ejemplo. Las del lexicón
 * traen `verbForms` (irregulares incluidos); las que detectó el coach llegan
 * sin, así que también se prueban las inflexiones regulares.
 */
function libInflections(w: LibWord, base: string): Set<string> {
  const b = base.toLowerCase();
  const forms = new Set([b, `${b}s`, `${b}es`, `${b}'s`]);
  if (w.verbForms) {
    const vf = w.verbForms;
    for (const f of [vf.base, vf.thirdPerson, vf.past, vf.pastParticiple, vf.gerund]) {
      if (f) forms.add(f.toLowerCase());
    }
  }
  if (b.endsWith('e')) {
    forms.add(`${b}d`);
    // take → taking, pero see → seeing (sin esto, «see» encontraba «sing»)
    if (/[^aeiouy]e$/.test(b)) forms.add(`${b.slice(0, -1)}ing`);
  } else {
    forms.add(`${b}ed`);
  }
  forms.add(`${b}ing`);
  if (/[^aeiou]y$/.test(b)) {
    forms.add(`${b.slice(0, -1)}ies`);
    forms.add(`${b.slice(0, -1)}ied`);
  }
  if (/[^aeiou][aeiou][bdgklmnprt]$/.test(b)) {
    forms.add(`${b}${b.slice(-1)}ed`);
    forms.add(`${b}${b.slice(-1)}ing`);
  }
  return forms;
}

/** Un token del ejemplo: puntuación pegada adelante, la palabra, puntuación pegada atrás. */
const CLOZE_TOKEN = /^([^\w'À-ÿ-]*)([\w'À-ÿ-]+)([^\w'À-ÿ-]*)$/;

/** Parte el ejemplo en el hueco, o null si la palabra no aparece (ni conjugada). */
function libClozeSplit(w: LibWord, answer: string): Pick<LibClozeRound, 'before' | 'filled' | 'after'> | null {
  if (!w.example || w.isPhrase || !answer) return null;
  const forms = libInflections(w, answer);
  const tokens = w.example.trim().split(/\s+/);
  for (let i = 0; i < tokens.length; i++) {
    const m = CLOZE_TOKEN.exec(tokens[i]!);
    if (!m || !forms.has(m[2]!.toLowerCase())) continue;
    // Los espacios quedan en before/after: se pinta `before`+hueco+`after` tal cual.
    const rest = tokens.slice(i + 1);
    return {
      before: `${tokens.slice(0, i).join(' ')}${i > 0 ? ' ' : ''}${m[1]!}`,
      filled: m[2]!,
      after: `${m[3]!}${rest.length > 0 ? ' ' : ''}${rest.join(' ')}`,
    };
  }
  return null;
}

/** Las palabras del deck con las que se puede armar una ronda (su ejemplo la contiene). */
export function libClozeable(words: readonly LibWord[]): LibWord[] {
  return words.filter((w) => libClozeSplit(w, libClozeForm(w)) !== null);
}

/** Formas distintas del deck que pueden ser opción (correcta o incorrecta). */
function libClozeOptionForms(pool: readonly LibWord[]): string[] {
  const seen = new Map<string, string>();
  for (const w of pool) {
    const f = libClozeForm(w);
    if (f.length > 1 && !seen.has(f.toLowerCase())) seen.set(f.toLowerCase(), f);
  }
  return [...seen.values()];
}

/**
 * Genera rondas cloze client-side ($0, sin LLM) desde `example`. Las 3
 * incorrectas se sortean entre TODO el deck (`pool`) en cada ronda: antes eran
 * siempre las primeras del deck y la respuesta salía por descarte.
 */
export function libClozeRounds(
  words: readonly LibWord[],
  pool: readonly LibWord[],
  rng: () => number = Math.random,
): LibClozeRound[] {
  const optionForms = libClozeOptionForms(pool);
  const rounds: LibClozeRound[] = [];
  for (const w of words) {
    if (rounds.length === 5) break;
    const answer = libClozeForm(w);
    const split = libClozeSplit(w, answer);
    if (!split) continue;
    const distract = libShuffle(
      optionForms.filter((p) => p.toLowerCase() !== answer.toLowerCase()),
      rng,
    ).slice(0, LIB_CLOZE_OPTIONS - 1);
    if (distract.length < LIB_CLOZE_OPTIONS - 1) continue;
    rounds.push({ word: w, ...split, answer, options: [answer, ...distract] });
  }
  return rounds;
}

/**
 * Por qué un juego NO se puede jugar con este deck (el texto que sigue a
 * «<deck>: »), o null si alcanza para al menos una ronda. Es la misma cuenta
 * que arma las rondas: un juego habilitado nunca abre en «0 de 0».
 */
export function libGameShortfall(
  game: LibGameDef,
  words: readonly LibWord[],
  deckId?: string,
): string | null {
  const n = words.length;
  const count = (k: number) => `${k} palabra${k === 1 ? '' : 's'}`;
  // «Mis guardadas» (Emmanuel, 20/10: «que lo diga claro»): cuántas guardó, cuántas pide y cómo sumar.
  if (deckId === 'guardadas' && n < game.min) {
    const have =
      n === 0 ? 'todavía no guardaste palabras' : `guardaste ${n === 1 ? '1 palabra' : `${n} palabras`}`;
    return `${have} y este juego necesita ${game.min}. Guardá más desde Explorar.`;
  }
  if (n < game.min) return `${count(n)} — necesita ${game.min}`;
  if (game.id !== 'hueco') return null;
  const distinct = libClozeOptionForms(words).length;
  if (distinct < LIB_CLOZE_OPTIONS) return `${count(distinct)} distintas — necesita ${LIB_CLOZE_OPTIONS}`;
  if (libClozeable(words).length === 0) return 'ninguna tiene una oración para completar';
  return null;
}

// ─── Shuffle (Fisher-Yates) ──────────────────────────────────────────
export function libShuffle<T>(arr: readonly T[], rng: () => number = Math.random): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}
